import type { Panorama, PanoramaProvider } from '../providers/types';
import type { LatLng } from './geo';
import type { GameMap } from './maps';

/**
 * Fetches the locations of a game one after the other and keeps the next one ready
 * while the player is busy with the current round.
 */
export class LocationQueue {
  private items: Promise<Panorama>[] = [];
  private readonly controller = new AbortController();

  constructor(
    readonly provider: PanoramaProvider,
    private readonly map: GameMap,
    private readonly rounds: number,
    /** Positions of the locations already played (possibly with another provider). */
    private readonly playedPositions: () => LatLng[],
    private readonly onReady?: (panorama: Panorama) => void,
  ) {}

  /** Location of round `index`; also starts fetching the following one. */
  get(index: number): Promise<Panorama> {
    while (this.items.length <= index) this.enqueue();
    if (index + 1 < this.rounds && this.items.length === index + 1) this.enqueue();
    return this.items[index];
  }

  /** Forgets the location of round `index` (and the ones after it) so a new one is fetched. */
  discardFrom(index: number): void {
    this.items.length = Math.min(this.items.length, index);
  }

  dispose(): void {
    this.controller.abort();
  }

  private enqueue(): void {
    const previous = this.items[this.items.length - 1] ?? Promise.resolve(null);
    const promise = previous
      .catch(() => null)
      .then(async (previousPanorama) => {
        if (this.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const exclude = [...this.playedPositions()];
        if (previousPanorama) exclude.push(previousPanorama.position);
        const panorama = await this.provider.findRandom(this.map, { signal: this.controller.signal, exclude });
        this.onReady?.(panorama);
        return panorama;
      });
    // Errors are handled by whoever awaits the item; avoid "unhandled rejection" noise for prefetches.
    promise.catch(() => undefined);
    this.items.push(promise);
  }
}

/** Warms the HTTP cache with the first image of a sphere panorama. */
export function prefetchPanoramaImage(panorama: Panorama): void {
  const url = panorama.imageUrls[0];
  if (!url || typeof fetch === 'undefined') return;
  fetch(url, { mode: 'cors', credentials: 'omit' }).catch(() => undefined);
}
